/**
 * Main-thread client for the OPFS Web Worker.
 * Provides a promise-based interface for OPFS file operations.
 *
 * Each request has a 30-second timeout (#35/#29) — except streaming
 * downloads, which get a longer budget. If the worker errors or times out,
 * the pending promise rejects and is cleaned up to prevent memory leaks.
 */

import { opfsAssetFileName } from './types';

export interface OpfsWriteResult {
  assetId: string;
  success: boolean;
  error?: string;
}

export interface OpfsReadResult {
  assetId: string;
  data: ArrayBuffer | null;
  error?: string;
}

export interface OpfsDeleteResult {
  assetId: string;
  success: boolean;
  error?: string;
}

export interface OpfsExistsResult {
  assetId: string;
  exists: boolean;
}

export interface OpfsDownloadResult {
  assetId: string;
  success: boolean;
  checksumVerified: boolean;
  error?: string;
}

/** Per-request timeout for OPFS operations (ms). */
const OPFS_REQUEST_TIMEOUT_MS = 30_000;

/**
 * Timeout for in-worker streaming downloads (ms). Sized for the largest
 * allowed asset (500MB) over a slow uplink — far beyond the 30s cap that
 * governs local file operations.
 */
const OPFS_DOWNLOAD_TIMEOUT_MS = 15 * 60_000;

let worker: Worker | null = null;
let messageId = 0;
const pendingResolvers = new Map<number, {
  resolve: (value: unknown) => void;
  reject: (reason: unknown) => void;
  timeout: ReturnType<typeof setTimeout>;
}>();

function getWorker(): Worker {
  if (!worker) {
    worker = new Worker(
      new URL('./opfs-worker.ts', import.meta.url),
      { type: 'module' },
    );
    worker.addEventListener('message', (event) => {
      const data = event.data as { type: string; _msgId?: number };
      const msgId = data._msgId;
      if (msgId !== undefined) {
        const resolver = pendingResolvers.get(msgId);
        if (resolver) {
          clearTimeout(resolver.timeout);
          pendingResolvers.delete(msgId);
          resolver.resolve(data);
        }
      }
    });
    worker.addEventListener('error', (event) => {
      console.error('[OPFS Client] Worker error:', event);
      // Reject all pending promises on worker error (#35/#29)
      for (const [id, resolver] of pendingResolvers) {
        clearTimeout(resolver.timeout);
        resolver.reject(new Error(`OPFS worker error: ${event.message}`));
        pendingResolvers.delete(id);
      }
    });
  }
  return worker;
}

function send(
  msg: Record<string, unknown>,
  timeoutMs: number = OPFS_REQUEST_TIMEOUT_MS,
): Promise<unknown> {
  return new Promise((resolve, reject) => {
    const id = ++messageId;
    msg._msgId = id;
    const timeout = setTimeout(() => {
      pendingResolvers.delete(id);
      reject(new Error(`OPFS operation timed out after ${timeoutMs}ms`));
    }, timeoutMs);
    pendingResolvers.set(id, { resolve, reject, timeout });
    getWorker().postMessage(msg);
  });
}

/**
 * Write data to OPFS via the Web Worker.
 */
export async function writeOpfsFile(
  assetId: string,
  data: ArrayBuffer,
): Promise<OpfsWriteResult> {
  return send({ type: 'write', assetId, data }) as Promise<OpfsWriteResult>;
}

/**
 * Read data from OPFS via the Web Worker.
 */
export async function readOpfsFile(assetId: string): Promise<OpfsReadResult> {
  return send({ type: 'read', assetId }) as Promise<OpfsReadResult>;
}

/**
 * Delete a file from OPFS via the Web Worker.
 */
export async function deleteOpfsFile(assetId: string): Promise<OpfsDeleteResult> {
  return send({ type: 'delete', assetId }) as Promise<OpfsDeleteResult>;
}

/**
 * Check if a file exists in OPFS via the Web Worker.
 */
export async function existsOpfsFile(assetId: string): Promise<OpfsExistsResult> {
  return send({ type: 'exists', assetId }) as Promise<OpfsExistsResult>;
}

/**
 * List all asset IDs stored in OPFS.
 */
export async function listOpfsFiles(): Promise<string[]> {
  const result = await send({ type: 'list' }) as { assetIds: string[] };
  return result.assetIds;
}

/**
 * Get the file size of an OPFS asset.
 */
export async function getOpfsFileSize(assetId: string): Promise<number> {
  const result = await send({ type: 'getSize', assetId }) as { size: number };
  return result.size;
}

/**
 * Stream a URL into OPFS inside the worker, verifying its SHA-256
 * incrementally. Replaces the buffer-the-whole-file download path: no
 * full-size ArrayBuffer is ever created on the main thread or the worker
 * (2GB-device target).
 */
export async function downloadOpfsFile(
  assetId: string,
  url: string,
  checksum: string,
): Promise<OpfsDownloadResult> {
  return send(
    { type: 'download', assetId, url, checksum },
    OPFS_DOWNLOAD_TIMEOUT_MS,
  ) as Promise<OpfsDownloadResult>;
}

/**
 * Get an asset's OPFS `File` handle directly on the main thread.
 *
 * A `File` is a disk-backed Blob: `URL.createObjectURL(file)` lets media
 * elements stream it with native range/seek support, without ever
 * materializing the file in RAM. Only `createSyncAccessHandle` requires a
 * worker — plain `getFile()` reads are main-thread-safe.
 */
export async function getOpfsFile(assetId: string): Promise<File | null> {
  try {
    const root = await navigator.storage.getDirectory();
    const handle = await root.getFileHandle(opfsAssetFileName(assetId));
    return await handle.getFile();
  } catch {
    return null;
  }
}

/**
 * Terminate the OPFS worker. Call during cleanup.
 */
export function terminateOpfsWorker(): void {
  if (worker) {
    worker.terminate();
    worker = null;
  }
  // Clear all pending timeouts and reject their promises
  for (const [, resolver] of pendingResolvers) {
    clearTimeout(resolver.timeout);
    resolver.reject(new Error('OPFS worker terminated'));
  }
  pendingResolvers.clear();
}
